import React, { useState, useEffect, useRef } from "react";
import { Lightbulb, CheckCircle, XCircle, Copy, Info, X, ChevronDown } from 'lucide-react';
import { useBilling } from '../../hooks/useBilling';
import "./screens.css";

const HomeScreen = ({
  recipe,
  setRecipe,
  createApp,
  recipeExamples,
  selectRecipe,
  goToAppList,
  isCreatingApp,
  completionMessage,
  clearCompletionMessage,
  failureMessage,
  clearFailureMessage,
  longWaitVisible,
  onLongWaitGoList,
  userId,
  goToPlan,
}) => {
  const { billingData, loading: billingLoading } = useBilling(userId);
  const textareaRef = useRef(null);
  const [isShared, setIsShared] = useState(false); // false = 個人データ, true = 共有データ
  const [isInfoExpanded, setIsInfoExpanded] = useState(false); // InfoBoxの開閉状態

  // テキストエリアの高さを調整する関数
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      // いずれかのモーダルが表示されているかチェック
      const hasAnyModal = isCreatingApp || !!completionMessage || !!failureMessage;
      
      if (hasAnyModal) {
        // モーダル表示中は最小高さに固定
        textareaRef.current.style.height = '6rem';
      } else {
        // 通常時は自動調整
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
      }
    }
  };

  // 全てのモーダル関連状態の変化を監視
  useEffect(() => {
    adjustTextareaHeight();
  }, [isCreatingApp, completionMessage, failureMessage]);

  // レシピの変化を監視（初期表示含む）
  useEffect(() => {
    adjustTextareaHeight();
  }, [recipe]);

  // 残り回数を計算
  const getRemainingCount = () => {
    if (!billingData) return null;
    const { monthlyQuota, usedThisPeriod, extraCredits } = billingData;
    return monthlyQuota - usedThisPeriod + extraCredits;
  };

  // 決済エラーチェック
  const hasPaymentError = () => {
    if (!billingData) return false;
    return billingData.subscription?.status === 'past_due';
  };

  // 使用状況ウィジェット
  const renderUsageWidget = () => {
    if (!userId) return null;

    // ローディング中
    if (billingLoading) {
      return (
        <div className="usage-widget usage-widget-loading">
          <div className="usage-widget-title">アプリ作成回数</div>
          <div className="usage-widget-loading-content">
            <div className="usage-widget-spinner"></div>
            <span className="usage-widget-loading-text">読み込み中...</span>
          </div>
        </div>
      );
    }

    if (!billingData) return null;

    const { plan, monthlyQuota, usedThisPeriod, extraCredits, subscription } = billingData;
    const remaining = getRemainingCount();
    const isExhausted = remaining <= 0;
    const isLow = remaining <= 2 && remaining > 0;
    const isPaymentError = hasPaymentError();

    const planNames = {
      trial: 'トライアル',
      light: 'ライト',
      creator: 'クリエイター'
    };

    return (
      <div
        className={`usage-widget ${isPaymentError ? 'usage-widget-exhausted' : isExhausted ? 'usage-widget-exhausted' : isLow ? 'usage-widget-warning' : ''}`}
        onClick={goToPlan}
        style={{ cursor: goToPlan ? 'pointer' : 'default' }}
        title="クリックしてプラン管理画面へ"
      >
        <div className="usage-widget-title">アプリ作成回数</div>
        <div className="usage-widget-header">
          <span className="usage-widget-plan">{planNames[plan] || plan}</span>
          <span className="usage-widget-remaining">
            残り {remaining}回
          </span>
        </div>
        {isPaymentError ? (
          <div className="usage-widget-notice usage-widget-notice-exhausted">
            ⚠️ お支払いエラー！タップして確認
          </div>
        ) : isExhausted ? (
          <div className="usage-widget-notice usage-widget-notice-exhausted">
            利用上限に達しました！タップしてプランを確認
          </div>
        ) : isLow && (
          <div className="usage-widget-notice">
            残りわずか！タップしてプランを確認
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="Homescreen home-container">
      <div className="Homescreen_Logo logo-container">
        <svg
          width="458"
          height="136"
          viewBox="0 0 458 136"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M62.7143 6.00239C88.5247 5.83455 111.092 20.5566 118.885 42.6479C122.85 53.8875 121.887 64.8553 115.927 76.3129C110.785 86.1993 100.67 96.1415 90.6059 101.202C85.4056 103.816 76.6434 106.276 70.9955 106.707C69.7402 106.804 69.7551 106.818 71.7279 107.444C77.797 109.366 84.545 109.597 90.0277 108.069C99.7131 105.368 109.89 97.6103 116.226 88.0981C119.739 82.8243 123.325 75.0084 125.116 68.7231L125.842 66.1723L125.869 69.7543C125.9 74.1564 125.352 79.3336 124.371 83.8969C122.138 94.2893 116.591 105.352 109.633 113.288C98.7802 125.666 85.8137 132.948 70.2641 135.397C65.6271 136.127 55.3056 136.213 51.0492 135.556C41.7732 134.126 33.3608 130.921 26.14 126.069C15.614 118.995 7.96365 109.296 3.51503 97.3852C1.10761 90.9392 0.0110463 84.6405 0.000383547 77.1987C-0.0157199 65.7001 3.04975 55.9667 9.30702 47.6489C16.3553 38.2801 27.3962 31.4967 36.974 30.6499L39.3383 30.4409L36.3178 32.062C32.252 34.2436 28.5971 37.1033 24.6605 41.184C14.4554 51.7633 8.91279 67.2644 10.2533 81.479C11.5885 95.6365 16.9699 106.442 27.6752 116.457C32.7437 121.2 39.1781 125.061 45.7152 127.284C49.739 128.651 50.3692 128.425 47.2582 126.729C39.0218 122.239 31.3538 113.574 26.6771 103.47C25.2374 100.359 23.199 94.2673 23.4975 93.9682C23.5938 93.8721 24.301 94.7426 25.0697 95.9028C30.3549 103.882 38.2014 110.922 46.2914 114.944C58.5329 121.029 73.6185 121.697 86.7943 116.738C90.4417 115.365 96.8834 111.891 96.5023 111.502C96.4155 111.417 94.9257 111.759 93.1918 112.261C87.8142 113.819 82.5085 114.425 76.4857 114.169C65.103 113.686 54.6277 110.409 46.1244 104.671C42.8222 102.443 37.5593 97.4435 35.0756 94.1752C32.572 90.8807 29.8267 85.1543 28.6635 80.8032C27.7869 77.524 27.6859 76.498 27.6713 70.7465C27.6614 66.7772 27.8357 63.5309 28.1303 62.2084C28.3911 61.037 28.5306 60.0044 28.4418 59.9116C28.1831 59.6571 25.4151 63.3962 23.4848 66.6079C21.9403 69.1783 19.6392 75.2317 18.7807 78.9819C18.3072 81.051 17.9937 76.7408 18.3139 72.5522C18.794 66.2704 20.5812 60.2989 23.4809 55.2885C31.9004 40.7397 46.9996 31.9952 62.2562 32.8315C64.5948 32.96 67.4444 33.309 68.5873 33.6069C71.601 34.3917 71.6812 34.3202 69.8822 32.4663C66.2888 28.7639 60.3523 25.0296 55.9906 23.728C47.5184 21.2001 38.483 21.5944 29.6547 24.8784C24.3142 26.865 14.3144 31.9738 11.3822 34.2133C9.67636 35.5162 9.86537 34.8363 12.1381 31.4946C14.9921 27.2981 21.4076 20.8282 25.7357 17.7817C29.3774 15.2179 36.8953 11.2903 40.9076 9.85493C44.4102 8.60182 50.2534 7.13237 54.1488 6.52485C55.8667 6.25736 59.7209 6.02179 62.7143 6.00239ZM56.7123 56.4018L40.3402 55.2875L46.9516 69.5522L40.3021 88.3403L59.1361 84.6684L74.3803 96.3217L75.4545 76.227L89.0228 66.6889L72.2484 61.392L69.1986 41.7006L56.7123 56.4018ZM69.8666 11.2485C66.9163 10.6077 54.9 10.1063 56.1732 10.6772C56.577 10.8578 58.4708 11.3587 60.3822 11.7915C77.9316 15.7648 93.5054 33.8104 97.0014 54.2221C99.1397 66.7083 96.2662 80.2869 89.642 88.9926C88.8222 90.0701 88.2148 91.0162 88.2924 91.0942C88.6395 91.4356 92.3582 89.205 94.6508 87.2797C106.076 77.6836 110.877 58.6807 105.89 42.8002C100.914 26.9555 87.3063 15.0376 69.8666 11.2485Z"
            fill="#FFD300"
          />
          <path
            d="M170.846 93.9177C168.37 93.7606 165.662 93.4201 162.723 92.8964C159.774 92.4251 156.566 91.7704 153.101 90.9324C157.118 83.9145 160.425 76.8441 163.022 69.7214C165.682 62.5463 167.707 55.3451 169.098 48.1176C170.771 48.0128 172.6 47.9343 174.583 47.8819C176.567 47.8295 178.385 47.8034 180.038 47.8034C181.755 47.751 182.933 47.7248 183.573 47.7248L181.034 60.9228C183.448 58.3565 185.875 55.5807 188.316 52.5955C190.767 49.5578 193.157 46.2845 195.486 42.7756C191.973 42.4613 188.163 42.3042 184.056 42.3042C178.136 42.3042 171.938 42.6446 165.46 43.3255C158.992 43.9539 152.656 44.8705 146.451 46.075L148.295 36.4908C154.295 36.0718 160.009 35.7576 165.436 35.5481C170.927 35.2862 176.098 35.1553 180.952 35.1553C187.458 35.1553 193.525 35.3648 199.151 35.7838C204.787 36.1504 209.849 36.7265 214.338 37.5121C211.997 42.7494 209.156 48.0914 205.815 53.5382C202.527 58.985 198.803 64.4841 194.641 70.0356C188.708 67.8884 183.964 65.7934 180.41 63.7509C177.755 74.2255 174.567 84.2811 170.846 93.9177ZM235.479 94.5462C229.913 91.8751 224.408 88.4709 218.964 84.3334C228.754 77.525 236.839 70.8474 243.218 64.3008C249.651 57.7542 254.889 50.7624 258.934 43.3255C254.347 43.3255 249.49 43.4826 244.363 43.7968C239.246 44.0587 234.184 44.4515 229.176 44.9752C224.232 45.4466 219.663 46.0489 215.469 46.7821L216.813 36.8836C221.44 36.6741 225.923 36.517 230.264 36.4122C234.604 36.3075 238.854 36.2551 243.014 36.2551C248.934 36.2551 254.669 36.3861 260.218 36.6479C265.778 36.8574 271.211 37.1717 276.517 37.5906C275.45 40.3664 274.573 42.5661 273.887 44.1896C273.265 45.7608 272.762 46.9916 272.377 47.8819C272.056 48.7199 271.723 49.4793 271.379 50.1601C269.139 50.1601 267.065 50.6839 265.156 51.7313C263.302 52.7788 261.755 54.1667 260.515 55.895C259.276 57.6233 258.46 59.5087 258.067 61.5512C257.815 62.8606 257.767 64.2223 257.921 65.6363C258.086 66.998 258.496 68.3335 259.15 69.6429C251.486 79.5413 243.596 87.8424 235.479 94.5462ZM268.504 70.507C265.571 70.507 263.354 69.6952 261.853 68.0717C260.415 66.3957 259.953 64.2223 260.467 61.5512C261.001 58.7755 262.314 56.5234 264.407 54.7951C266.499 53.0668 268.932 52.2027 271.706 52.2027C274.532 52.2027 276.676 53.1192 278.137 54.9523C279.661 56.7329 280.156 59.0112 279.622 61.7869C279.128 64.3532 277.872 66.4481 275.853 68.0717C273.834 69.6952 271.384 70.507 268.504 70.507ZM268.914 67.129C269.767 67.129 270.597 66.4219 271.402 65.0079C272.208 63.5938 272.817 61.8131 273.23 59.6658C273.462 58.4612 273.423 57.4138 273.114 56.5234C272.806 55.6331 272.358 55.1879 271.771 55.1879C270.971 55.1879 270.1 55.9735 269.158 57.5447C268.216 59.1159 267.553 60.8966 267.17 62.8867C266.959 63.9866 267.007 64.9817 267.316 65.872C267.688 66.71 268.221 67.129 268.914 67.129ZM317.192 94.0748C313.38 90.8801 309.631 86.664 305.945 81.4268C313.167 77.7083 319.055 74.4088 323.61 71.5283C328.164 68.6478 331.569 66.0553 333.826 63.7509C336.146 61.3941 337.497 59.2206 337.88 57.2305C338.354 54.7689 337.553 52.8312 335.479 51.4171C333.468 49.9506 330.542 49.2174 326.702 49.2174C322.915 49.2174 317.623 49.9768 310.824 51.4956C304.035 52.9621 296.097 55.1356 287.009 58.0161L286.892 49.0603C290.423 48.17 294.1 47.3582 297.921 46.625C301.742 45.8917 305.516 45.2633 309.243 44.7395C312.971 44.2158 316.465 43.7968 319.725 43.4826C323.039 43.1684 325.896 43.0112 328.296 43.0112C333.789 43.0112 338.465 43.7968 342.323 45.368C346.19 46.8868 349.031 49.0341 350.843 51.8099C352.656 54.5856 353.209 57.8066 352.504 61.4727C351.789 65.1912 349.816 69.0667 346.587 73.0995C343.368 77.0798 339.219 80.903 334.141 84.5691C329.125 88.1829 323.476 91.3514 317.192 94.0748ZM384.913 97.0601C382.519 92.8702 380.006 89.0208 377.374 85.5118C374.743 82.0029 371.759 78.5201 368.424 75.0634C365.153 71.5545 361.282 67.836 356.811 63.908C360.341 59.1421 363.827 54.7428 367.27 50.7101C370.712 46.6773 374.311 42.8017 378.067 39.0833C381.875 35.3648 385.977 31.6463 390.373 27.9278C392.922 28.8182 395.398 29.9442 397.803 31.3059C400.271 32.6152 402.651 34.1078 404.941 35.7838C398.439 39.9212 392.329 44.3729 386.612 49.1389C380.905 53.8524 375.216 59.1683 369.544 65.0864C372.234 67.1813 374.656 69.1453 376.81 70.9784C378.974 72.759 381.027 74.5659 382.968 76.399C384.962 78.232 386.974 80.2484 389.004 82.448C391.044 84.5953 393.317 87.0568 395.823 89.8326C394.035 91.0896 392.256 92.2941 390.488 93.4463C388.773 94.5985 386.914 95.8031 384.913 97.0601ZM413.959 75.5348C414.516 68.2026 415.322 60.6871 416.376 52.9883C417.441 45.2371 418.749 37.3288 420.301 29.2634H440.141C438.589 37.3288 436.828 45.2371 434.856 52.9883C432.949 60.6871 430.863 68.2026 428.599 75.5348H413.959ZM410.163 93.6034L412.037 83.8621H427.397L425.523 93.6034H410.163Z"
            fill="#FFD300"
          />
          <path d="M429 105H146L139 123L429 110V105Z" fill="#FFD300" />
        </svg>
      </div>

      {/* アプリ作成フォーム */}
      <div className="Homescreen_Form form-container">
        {/* アプリ作成中モーダル - 追加部分 */}
        {isCreatingApp && (
          <div className="Homescreen_Modal modal-overlay">
            <div className="Homescreen_ModalContent modal-content">
              <ProgressBar />
            </div>
          </div>
        )}
        
        {/* アプリ作成完了メッセージモーダル */}
        {completionMessage && (
          <div className="Homescreen_Modal modal-overlay">
            <div className="Homescreen_ModalContent modal-content Homescreen_CompletionModal completion-modal">
              <h3 className="Homescreen_CompletionTitle completion-title"><CheckCircle className="modal-title-icon" /> 作成完了！</h3>
              <p className="Homescreen_CompletionMessage completion-message">{completionMessage}</p>
              <div className="Homescreen_ModalActions modal-actions">
                <button
                  className="Homescreen_ModalButton modal-button Homescreen_ModalButton--primary modal-button-primary"
                  onClick={() => {
                    goToAppList();
                    clearCompletionMessage();
                  }}
                >
                  アプリ一覧を見る
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 失敗メッセージモーダル */}
        {failureMessage && (
          <div className="Homescreen_Modal modal-overlay">
            <div className="Homescreen_ModalContent modal-content Homescreen_FailureModal failure-modal">
              <h3 className="Homescreen_FailureTitle failure-title"><XCircle className="modal-title-icon" /> エラー</h3>
              <p className="Homescreen_FailureMessage failure-message">{failureMessage}</p>
              <div className="Homescreen_ModalActions modal-actions">
                <button 
                  className="Homescreen_ModalButton modal-button Homescreen_ModalButton--primary modal-button-primary"
                  onClick={() => {
                    clearFailureMessage();
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 長時間作成中（中立）モーダル */}
        {longWaitVisible && (
          <div className="Homescreen_Modal modal-overlay">
            <div className="Homescreen_ModalContent modal-content Homescreen_LongWaitModal longwait-modal">
              <h3 className="Homescreen_FailureTitle failure-title"><Info className="modal-title-icon" /> 長時間作成中</h3>
              <p className="Homescreen_FailureMessage failure-message">作成に時間がかかっています。しばらくしてからアプリ一覧を更新してみてください。</p>
              <div className="Homescreen_ModalActions modal-actions">
                <button 
                  className="Homescreen_ModalButton modal-button Homescreen_ModalButton--primary modal-button-primary"
                  onClick={onLongWaitGoList}
                >
                  アプリ一覧へ
                </button>
              </div>
            </div>
          </div>
        )}

        <h3 className="Homescreen_FormLabel form-label">レシピからつくる</h3>
        <textarea
          ref={textareaRef}
          className="Homescreen_Textarea recipe-textarea"
          placeholder="例：友だちと日程を合わせるアプリ"
          value={recipe}
          onChange={(e) => {
            setRecipe(e.target.value);
          }}
          disabled={isCreatingApp}
          rows="3"
        />

        <div className="Homescreen_ButtonContainer create-button-container">
          <button
            className={`Homescreen_Button create-button ${
              isCreatingApp || !recipe.trim() || (getRemainingCount() !== null && getRemainingCount() <= 0) || hasPaymentError()
                ? "Homescreen_Button--disabled create-button-disabled"
                : "Homescreen_Button--enabled create-button-enabled"
            }`}
            onClick={() => {
              // 決済エラーチェック
              if (hasPaymentError()) {
                alert('お支払いに問題があります。\n\nプラン画面から支払い方法を更新してください。');
                if (goToPlan) goToPlan();
                return;
              }
              // 残量チェック
              const remaining = getRemainingCount();
              if (remaining !== null && remaining <= 0) {
                alert('利用上限に達しました。\n\nプラン画面からアップグレードまたは追加クレジットを購入できます。');
                if (goToPlan) goToPlan();
                return;
              }
              createApp(isShared ? "true" : "false");
            }}
            disabled={isCreatingApp || !recipe.trim() || !billingData || getRemainingCount() <= 0 || hasPaymentError()}
          >
            アプリを作成
          </button>
        </div>

        {/* トライアルプラン7日制限の注意書き */}
        {billingData && billingData.plan === 'trial' && (
          <div className="Homescreen_TrialNotice trial-notice">
            ⚠️ トライアルプランで作成したアプリは、作成後7日間のみ利用可能です。<br />
            有料プランにアップグレードすると、過去のアプリも含めて制限なく利用できます。
          </div>
        )}

        {/* データ保存方法の選択 */}
        <div className="Homescreen_DataTypeSection data-type-section">
          <label className="Homescreen_DataTypeLabel data-type-label">データの保存方法:</label>
          <div className="Homescreen_ToggleContainer toggle-container">
            <span 
              className={`Homescreen_ToggleOption toggle-option ${!isShared ? 'Homescreen_ToggleOption--active Homescreen_ToggleOption--personal active personal' : ''}`}
              onClick={() => setIsShared(false)}
            >
              個人データ
            </span>
            <div className="Homescreen_ToggleSwitch toggle-switch" onClick={() => setIsShared(!isShared)}>
              <div className={`Homescreen_ToggleSlider toggle-slider ${isShared ? 'Homescreen_ToggleSlider--shared shared' : 'Homescreen_ToggleSlider--personal personal'}`}></div>
            </div>
            <span 
              className={`Homescreen_ToggleOption toggle-option ${isShared ? 'Homescreen_ToggleOption--active Homescreen_ToggleOption--shared active shared' : ''}`}
              onClick={() => setIsShared(true)}
            >
              共有データ
            </span>
          </div>
          <p className="Homescreen_DataTypeDescription data-type-description">
            {isShared 
              ? "• 同じアプリを使う全ての人とデータが共有されます" 
              : "• データは使う人のブラウザにのみ保存されます"
            }
          </p>
        </div>
        
        {/* アプリ制限についての情報ボックス */}
        <div className="Homescreen_InfoBox info-box">
          <div className="info-content">
            <h4
              className="info-title info-toggle"
              onClick={() => setIsInfoExpanded(!isInfoExpanded)}
            >
              <Info className="info-item-icon" />生成に失敗するときは？
              <ChevronDown className={`info-toggle-icon ${isInfoExpanded ? 'expanded' : ''}`} />
            </h4>
            <div className={`info-items ${isInfoExpanded ? 'expanded' : ''}`}>
              <p className="info-item">・1画面で完結するシンプルなWebアプリしか作れません。（通知やメール等は❌）</p>
              <p className="info-item">・同じレシピでも成功したり、失敗したりすることがあります。</p>
              <p className="info-item">・みんなで使うアプリを作ったのに「個人データ」を選んでしまうと、データを共有できません。</p>
              <p className="info-item">・1日で作れるアプリ数に上限があります。</p>
            </div>
          </div>
        </div>

        {/* 使用状況ウィジェット */}
        {renderUsageWidget()}
      </div>

      {/* recipeExamplesセクションを修正 */}
      <h3 className="Homescreen_ExamplesTitle examples-title">こんなのつくれるよ</h3>
      <div className="Homescreen_ExamplesList examples-list">
        {recipeExamples.map((example) => (
          <div
            key={example.id}
            className="Homescreen_ExampleItem example-item"
            onClick={() => {
              // エリア全体のクリックでURLに遷移（新しいウィンドウで開く）
              // クリックでURLに遷移（window.open()はLINE内で使えないためLIFFを使用）
              if (example.url) {
                liff.openWindow({
                  url: example.url,
                  external: true
                });
              }
            }}
          >
            <div className="Homescreen_ExampleIcon example-icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 126 130"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M62.7143 0.00239304C88.5247 -0.165448 111.092 14.5566 118.885 36.6479C122.85 47.8875 121.887 58.8553 115.927 70.3129C110.785 80.1993 100.67 90.1415 90.6059 95.2016C85.4056 97.8161 76.6434 100.276 70.9955 100.707C69.7402 100.804 69.7551 100.818 71.7279 101.444C77.797 103.366 84.545 103.597 90.0277 102.069C99.7131 99.3684 109.89 91.6103 116.226 82.0981C119.739 76.8243 123.325 69.0084 125.116 62.7231L125.842 60.1723L125.869 63.7543C125.9 68.1564 125.352 73.3336 124.371 77.8969C122.138 88.2893 116.591 99.352 109.633 107.288C98.7802 119.666 85.8137 126.948 70.2641 129.397C65.6271 130.127 55.3056 130.213 51.0492 129.556C41.7732 128.126 33.3608 124.921 26.14 120.069C15.614 112.995 7.96365 103.296 3.51503 91.3852C1.10761 84.9392 0.0110463 78.6405 0.000383547 71.1987C-0.0157199 59.7001 3.04975 49.9667 9.30702 41.6489C16.3553 32.2801 27.3962 25.4967 36.974 24.6499L39.3383 24.4409L36.3178 26.062C32.252 28.2436 28.5971 31.1033 24.6605 35.184C14.4554 45.7633 8.91279 61.2644 10.2533 75.479C11.5885 89.6365 16.9699 100.442 27.6752 110.457C32.7437 115.2 39.1781 119.061 45.7152 121.284C49.739 122.651 50.3692 122.425 47.2582 120.729C39.0218 116.239 31.3538 107.574 26.6771 97.4702C25.2374 94.359 23.199 88.2673 23.4975 87.9682C23.5938 87.8721 24.301 88.7426 25.0697 89.9028C30.3549 97.8818 38.2014 104.922 46.2914 108.944C58.5329 115.029 73.6185 115.697 86.7943 110.738C90.4417 109.365 96.8834 105.891 96.5023 105.502C96.4155 105.417 94.9257 105.759 93.1918 106.261C87.8142 107.819 82.5085 108.425 76.4857 108.169C65.103 107.686 54.6277 104.409 46.1244 98.6713C42.8222 96.4432 37.5593 91.4435 35.0756 88.1752C32.572 84.8807 29.8267 79.1543 28.6635 74.8032C27.7869 71.524 27.6859 70.498 27.6713 64.7465C27.6614 60.7772 27.8357 57.5309 28.1303 56.2084C28.3911 55.037 28.5306 54.0044 28.4418 53.9116C28.1831 53.6571 25.4151 57.3962 23.4848 60.6079C21.9403 63.1783 19.6392 69.2317 18.7807 72.9819C18.3072 75.051 17.9937 70.7408 18.3139 66.5522C18.794 60.2704 20.5812 54.2989 23.4809 49.2885C31.9004 34.7397 46.9996 25.9952 62.2562 26.8315C64.5948 26.96 67.4444 27.309 68.5873 27.6069C71.601 28.3917 71.6812 28.3202 69.8822 26.4663C66.2888 22.7639 60.3523 19.0296 55.9906 17.728C47.5184 15.2001 38.483 15.5944 29.6547 18.8784C24.3142 20.865 14.3144 25.9738 11.3822 28.2133C9.67636 29.5162 9.86537 28.8363 12.1381 25.4946C14.9921 21.2981 21.4076 14.8282 25.7357 11.7817C29.3774 9.21793 36.8953 5.29032 40.9076 3.85493C44.4102 2.60182 50.2534 1.13237 54.1488 0.524854C55.8667 0.257364 59.7209 0.0217922 62.7143 0.00239304ZM56.7123 50.4018L40.3402 49.2875L46.9516 63.5522L40.3021 82.3403L59.1361 78.6684L74.3803 90.3217L75.4545 70.227L89.0228 60.6889L72.2484 55.392L69.1986 35.7006L56.7123 50.4018ZM69.8666 5.24849C66.9163 4.60767 54.9 4.10627 56.1732 4.6772C56.577 4.85783 58.4708 5.35869 60.3822 5.79146C77.9316 9.76484 93.5054 27.8104 97.0014 48.2221C99.1397 60.7083 96.2662 74.2869 89.642 82.9926C88.8222 84.0701 88.2148 85.0162 88.2924 85.0942C88.6395 85.4356 92.3582 83.205 94.6508 81.2797C106.076 71.6836 110.877 52.6807 105.89 36.8002C100.914 20.9555 87.3063 9.03765 69.8666 5.24849Z"
                  fill="#FFD300"
                />
              </svg>
            </div>
            <div className="Homescreen_ExampleContent example-content">
              <p className="Homescreen_ExampleTitle example-title">{example.title}</p>
            </div>

            {/* 右側に「レシピ」ボタンを配置 */}
            <button
              className="Homescreen_RecipeButton recipe-button"
              onClick={async (e) => {
                e.stopPropagation(); // クリックイベントの伝播を停止
                
                // クリップボードにコピー
                try {
                  await navigator.clipboard.writeText(example.recipe);
                  // アラート表示
                  alert(`レシピをコピーしました！\n\n「${example.recipe}」`);
                } catch (err) {
                  // navigator.clipboardが使えない場合はアラートのみ表示
                  alert(`レシピをコピーできませんでした。\n\n「${example.recipe}」`);
                }
              }}
            >
              <Copy className="recipe-button-icon" />レシピ
            </button>
          </div>
        ))}
      </div>

    </div>
  );
};

// プログレスバーコンポーネント
const ProgressBar = () => {
  const [progress, setProgress] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isBackground, setIsBackground] = useState(() => typeof document !== 'undefined' ? document.hidden : false);
  
  // 3分（180秒）で100%になるように設定
  const totalTime = 180; // 秒
  
  // Tips配列
  const tips = [
    // 最初に必ず表示される重要な情報
    "アプリ生成に時間がかかる場合、この画面を閉じて、あとで戻ってきてもOKです。",
    // 基本的な使い方のTips
    "作ったアプリはURLを送るだけで友達とシェアできます",
    "URLをLINEやXで送れば、みんなで使えます",
    "AIなので、同じレシピでも毎回違うアプリになります",
    "共有データを選ぶとみんなで同じデータを使えます",
    "共有データなら、友達の入力もリアルタイムで反映",
    "個人データを選ぶとブラウザごとに別々のデータを保存するよ",
    "個人データはブラウザに保存されるので、他の人には見えません",
    "データ保存方法は後から変更できないので要注意",
    // アプリ作成のコツ
    "10個程度の箇条書きにすると、AIが理解しやすいよ",
    "詳しく書きすぎてもAIが混乱するので簡潔に",
    "「〜みたいな」「〜的な」で参考イメージを伝えるのも効果的",
    "似たアプリの名前を出すと、AIがイメージを掴みやすいよ",
    "ボタンの配置や色の希望も書くと反映されやすいです",
    "スマホで使うことを想定して作られています",
    "レシピに絵文字を入れると楽しいアプリになるかも？",
    "🎮🎯🎨 絵文字でアプリの雰囲気を伝えよう",
    "機能を詰め込みすぎると、失敗する可能性が高くなるよ",
    "300字前後のレシピが、成功する確率が高いみたい",
    // システムの特徴
    "1日に作れるアプリ数には上限があります",
    "作成には通常1〜3分かかります",
    "複雑なアプリほど作成に時間がかかります",
    "アプリ一覧から過去の作品をいつでも確認できます",
    "AIが頑張って作成中...もう少しお待ちください"
  ];
  
  // 可視状態の監視
  useEffect(() => {
    const onVis = () => setIsBackground(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // プログレスバー更新用のEffect（前景のみ動作）
  useEffect(() => {
    if (isBackground) return; // バックグラウンドではタイマー停止

    const timer = setInterval(() => {
      const startTime = Number(localStorage.getItem('creatingStartTime') || 0);
      const elapsedTime = (Date.now() - startTime) / 1000; // 経過時間（秒）
      const newProgress = Math.min((elapsedTime / totalTime) * 100, 100);
      setProgress(newProgress);
      if (newProgress >= 100) clearInterval(timer);
    }, 500);

    return () => clearInterval(timer);
  }, [isBackground]);
  
  // Tips切り替え用のEffect（前景のみ動作）
  useEffect(() => {
    if (isBackground) return; // バックグラウンドでは停止

    // 初回は必ず最初のTip（重要な情報）を表示
    setCurrentTipIndex(0);

    const tipTimer = setInterval(() => {
      setIsVisible(false); // フェードアウト
      setTimeout(() => {
        setCurrentTipIndex(prevIndex => {
          let newIndex;
          do {
            newIndex = Math.floor(Math.random() * tips.length);
          } while (newIndex === prevIndex && tips.length > 1);
          return newIndex;
        });
        setIsVisible(true); // フェードイン
      }, 300);
    }, 15000);

    return () => clearInterval(tipTimer);
  }, [tips.length, isBackground]);
  
  return (
    <>
      <p className="progress-text">アプリを作成しています...</p>
      <div className="progress-container">
        <div 
          className="progress-bar" 
          style={{ width: `${progress}%` }}
        >
        </div>
      </div>
      <p className="progress-percentage">
        {progress >= 100 ? "仕上げ作業をしています..." : `${Math.round(progress)}%`}
      </p>
      <div className="progress-tip-container">
        <p className={`progress-tip ${isVisible ? 'progress-tip--visible' : ''}`}>
          <Lightbulb className="progress-tip-icon" />ヒント：{tips[currentTipIndex]}
        </p>
      </div>
    </>  
  );
};

export default HomeScreen;
